import { Helmet } from 'react-helmet-async';
import { siteConfig, getFullUrl } from '@/config/site';

/**
 * Organization Schema - for homepage
 */
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: getFullUrl('/'),
    logo: getFullUrl(siteConfig.ogImage),
    description: siteConfig.description,
    foundingDate: siteConfig.foundingYear.toString(),
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteConfig.email,
      contactType: 'customer service',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: siteConfig.averageRating.toString(),
      reviewCount: siteConfig.reviewCount.toString(),
      bestRating: '5',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Product Schema - for product pages
 */
interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  slug: string;
  rating?: number;
  reviewCount?: number;
}

export function ProductSchema({
  name,
  description,
  image,
  price,
  currency = 'EUR',
  slug,
  rating = 4.9,
  reviewCount = 250,
}: ProductSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: getFullUrl(image),
    brand: {
      '@type': 'Brand',
      name: siteConfig.name,
    },
    offers: {
      '@type': 'Offer',
      price: price.toFixed(2),
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
      url: getFullUrl(`/shop/id-card-generator/${slug}`),
      seller: {
        '@type': 'Organization',
        name: siteConfig.name,
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'DE',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'DE',
        },
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'EUR',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 2,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 2,
            maxValue: 5,
            unitCode: 'DAY',
          },
        },
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating.toString(),
      reviewCount: reviewCount.toString(),
      bestRating: '5',
    },
    review: [
      { '@type': 'Review', author: { '@type': 'Person', name: 'Maximilian S.' }, datePublished: '2025-11-12', reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }, reviewBody: 'Excellent quality card, very impressed with the details and fast delivery.' },
      { '@type': 'Review', author: { '@type': 'Person', name: 'Sophie L.' }, datePublished: '2025-10-28', reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }, reviewBody: 'Amazing holograms and print quality. Arrived within a week to Germany.' },
      { '@type': 'Review', author: { '@type': 'Person', name: 'Thomas K.' }, datePublished: '2025-09-15', reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }, reviewBody: 'Best novelty card I have ever ordered. The scannable barcode works perfectly.' },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * FAQ Schema - for FAQ sections
 */
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

export function FAQSchema({ items }: FAQSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Review Schema - for reviews page
 */
interface Review {
  author: string;
  rating: number;
  text: string;
  date?: string;
}

interface ReviewSchemaProps {
  reviews: Review[];
  productName?: string;
}

export function ReviewSchema({ reviews, productName = 'Fake-ID.com Novelty Cards' }: ReviewSchemaProps) {
  const avgRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      reviewCount: reviews.length.toString(),
      bestRating: '5',
    },
    review: reviews.slice(0, 10).map(review => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating.toString(),
        bestRating: '5',
      },
      reviewBody: review.text,
      datePublished: review.date || new Date().toISOString().split('T')[0],
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Breadcrumb Schema - for navigation hierarchy
 */
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: getFullUrl(item.url),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Website Schema - for sitelinks search box
 */
export function WebsiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: getFullUrl('/'),
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${getFullUrl('/shop')}?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * LocalBusiness Schema (optional - if physical location exists)
 */
export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'OnlineBusiness',
    name: siteConfig.name,
    url: getFullUrl('/'),
    email: siteConfig.email,
    priceRange: '€€',
    image: getFullUrl(siteConfig.ogImage),
    description: siteConfig.description,
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Article Schema - for blog articles (JSON-LD)
 */
interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  slug: string;
  language: string;
  datePublished?: string;
  dateModified?: string;
}

export function ArticleSchema({
  title,
  description,
  image,
  slug,
  language,
  datePublished,
  dateModified,
}: ArticleSchemaProps) {
  const published = datePublished || '2025-01-15';
  const modified = dateModified || new Date().toISOString().split('T')[0];
  const articleUrl = language === siteConfig.defaultLanguage
    ? getFullUrl(`/blog/${slug}`)
    : getFullUrl(`/${language}/blog/${slug}`);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image.startsWith('http') ? image : getFullUrl(image),
    url: articleUrl,
    inLanguage: language,
    datePublished: published,
    dateModified: modified,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: getFullUrl('/'),
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: getFullUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: getFullUrl(siteConfig.ogImage),
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * SiteNavigationElement Schema - signals primary navigation to Google for sitelinks
 */
export function SiteNavigationSchema() {
  const navItems = [
    { name: 'Shop', url: '/shop' },
    { name: 'Reviews', url: '/reviews' },
    { name: 'Blog', url: '/blog' },
    { name: 'How To', url: '/how-to-order' },
    { name: 'About', url: '/about' },
    { name: 'Contact', url: '/contact' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: navItems.map((item, index) => ({
      '@type': 'SiteNavigationElement',
      position: index + 1,
      name: item.name,
      url: getFullUrl(item.url),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
